import { useAccountContext } from '@/lib/accounts/context';
import { getCellByPipelineId, isRecordsLikeCell } from '@/core/cell';
import { getNodes, useExplorationDataQuery } from '@/graphql';
import { EnumVariableDefinition, RecordsLikeCell } from '@/explore/types';
import { filterVariablesForPipeline } from '@/explore/utils';
import { dereferencePipeline } from '@/explore/pipeline/utils';
import { Select, withEmptyOptionForMissingValue } from '@/components/form/select';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';

import { useExplorationContext } from '../exploration-context';

interface EnumInputProps {
  variable: EnumVariableDefinition;
  value: EnumVariableDefinition['defaultValue'];
  onChange: (value: EnumVariableDefinition['defaultValue']) => void;
}

export const EnumInput = (props: EnumInputProps) => {
  const { variable } = props;
  const { exploration } = useExplorationContext();

  const numericOptions = variable.options.every(({ value }) => typeof value === 'number');

  const handleChange = (value: string | number) => {
    props.onChange(numericOptions ? Number(value) : value);
  };

  // This should be props.variable.source === 'static' once we parse exploration state with zod schema.
  if (variable.source !== 'query') {
    return (
      <Select
        value={String(props.value)}
        onChange={handleChange}
        options={withEmptyOptionForMissingValue(
          props.variable.options.map(({ value }) => ({
            label: String(value),
            value: String(value),
          })),
          String(props.value),
        )}
      />
    );
  }

  const cell = getCellByPipelineId(exploration.view.cells, props.variable.queryOptions.pipelineId);

  if (cell === undefined || !isRecordsLikeCell(cell)) {
    return <Select value={String(props.value)} onChange={handleChange} options={[]} />;
  }

  return <DynamicSelect cell={cell} {...props} />;
};

const DynamicSelect = (props: EnumInputProps & { cell: RecordsLikeCell }) => {
  const { exploration, getVariables } = useExplorationContext();
  const { account, isFeatureEnabled } = useAccountContext();
  const pipeline = dereferencePipeline(props.cell.pipeline, exploration);
  const variables = filterVariablesForPipeline(pipeline, getVariables());

  const queryVariables = useMemoizeDataQueryVariables({
    accountId: account.accountId,
    baseModelId: pipeline.baseModelId,
    pipeline: [
      ...pipeline.operations,
      {
        operation: 'groupAggregate',
        parameters: {
          groups: [{ key: props.variable.queryOptions.field }],
          aggregations: [
            { type: 'count', property: { key: '_sup_variable_distinct', name: 'Count' } },
          ],
        },
      },
    ],
    variables,
    options: {
      labels: { exploration: exploration.explorationId, block: props.cell.id },
      optimize: isFeatureEnabled('optimizeQuery'),
    },
    first: 500,
  });

  const { data } = useExplorationDataQuery({
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
  });

  const options = getNodes(data?.account?.query)
    ?.map((item) => item[props.variable.queryOptions.field])
    .map((item) => ({ value: item, label: String(item ?? '') }))
    .sort((a, b) => a.label.localeCompare(String(b.label)));

  const numericOptions = options.every(({ value }) => typeof value === 'number');

  return (
    <Select
      value={String(props.value)}
      onChange={(value) => props.onChange(numericOptions ? Number(value) : value)}
      options={withEmptyOptionForMissingValue(options, String(props.value))}
    />
  );
};
